import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Statistiques du bouton « Réserver » d'un marchand :
//   • visiteurs   → sessions distinctes ayant vu le bouton ;
//   • appels      → nombre de clics sur le bouton ;
//   • réservations → rendez-vous réellement créés ce mois-ci.
// ─────────────────────────────────────────────────────────────

export interface ReserveStats {
  visitors: number;
  clicks: number;
  monthlyBookings: number;
}

const EMPTY: ReserveStats = { visitors: 0, clicks: 0, monthlyBookings: 0 };

export function useReserveStats(merchantId: number | null) {
  const [stats, setStats] = useState<ReserveStats>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!merchantId) {
      setStats(EMPTY);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // 1) Événements du bouton Réserver
      const { data: events, error: evErr } = await supabase
        .from('reserve_button_events')
        .select('session_id, event')
        .eq('boutique_id', merchantId);
      if (evErr) throw evErr;

      const rows = (events || []) as { session_id: string | null; event: string | null }[];
      const viewSessions = new Set<string>();
      let clicks = 0;
      rows.forEach((r) => {
        if (r.event === 'click') clicks += 1;
        else if (r.event === 'view' && r.session_id) viewSessions.add(r.session_id);
      });

      // 2) Réservations créées ce mois-ci (via les services du marchand)
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      let monthlyBookings = 0;
      const { data: services, error: svcErr } = await supabase
        .from('booking_services')
        .select('id')
        .eq('owner', merchantId);
      if (svcErr) throw svcErr;

      const serviceIds = ((services || []) as { id: number }[]).map((s) => s.id);
      if (serviceIds.length > 0) {
        const { count, error: apptErr } = await supabase
          .from('booking_appointments')
          .select('id', { count: 'exact', head: true })
          .in('service_id', serviceIds)
          .gte('created_at', monthStart.toISOString());
        if (apptErr) throw apptErr;
        monthlyBookings = count || 0;
      }

      setStats({ visitors: viewSessions.size, clicks, monthlyBookings });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des statistiques.');
      setStats(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { stats, loading, error, reload: refresh };
}